/**
 * Search — §9.
 *
 * Two passes over the same 6,236 rows. The skeleton pass is consulted ONLY when the
 * precise pass is empty; consulting it always would bury exact matches under
 * over-matches.
 */
import { normalise, skeleton } from './arabic'
import { SEARCH_DEFAULT_LIMIT, SEARCH_MIN_QUERY_LENGTH } from './constants'
import type { SearchRow } from './types'

export type SearchMode = 'exact' | 'skeleton'

export interface SearchResult {
  mode: SearchMode | null
  /** The rows to render — at most `limit` of them. */
  hits: SearchRow[]
  /** The TRUE number of matching ayat. L15: never report the render cap as the total. */
  total: number
  /** The TRUE number of non-overlapping occurrences across all matching ayat. */
  occurrences: number
  /** Whether `hits` was cut short of `total`. */
  truncated: boolean
  /** The folded query actually used, so the highlighter folds the same way. */
  needle: string
}

const EMPTY: SearchResult = {
  mode: null,
  hits: [],
  total: 0,
  occurrences: 0,
  truncated: false,
  needle: '',
}

/** Count non-overlapping occurrences — advance past each match, which is how a reader counts a word. */
export function countOccurrences(haystack: string, needle: string): number {
  if (!needle) return 0
  let count = 0
  let from = 0
  for (;;) {
    const at = haystack.indexOf(needle, from)
    if (at === -1) return count
    count += 1
    from = at + needle.length
  }
}

/**
 * Walk every row, but run the occurrence count only on rows already known to match —
 * so per-keystroke cost stays tied to the hit count rather than to 6,236.
 */
function scan(
  rows: readonly SearchRow[],
  field: 'n' | 's',
  needle: string,
  limit: number,
): Omit<SearchResult, 'mode'> {
  const hits: SearchRow[] = []
  let total = 0
  let occurrences = 0

  for (const row of rows) {
    const value = row[field]
    if (!value.includes(needle)) continue
    total += 1
    occurrences += countOccurrences(value, needle)
    // L15: keep at most `limit` rows, but keep counting every match.
    if (hits.length < limit) hits.push(row)
  }

  return { hits, total, occurrences, truncated: total > hits.length, needle }
}

export function search(
  rows: readonly SearchRow[],
  query: string,
  limit: number = SEARCH_DEFAULT_LIMIT,
): SearchResult {
  const q = normalise(query).trim()
  if (q.length < SEARCH_MIN_QUERY_LENGTH) return EMPTY

  const exact = scan(rows, 'n', q, limit)
  if (exact.total > 0) return { mode: 'exact', ...exact }

  const s = skeleton(query).trim()
  if (!s) return EMPTY

  const loose = scan(rows, 's', s, limit)
  return loose.total > 0 ? { mode: 'skeleton', ...loose } : EMPTY
}

// ---------------------------------------------------------------------------
// Highlighting
// ---------------------------------------------------------------------------

export interface Highlight {
  before: string
  match: string
  after: string
}

/**
 * The index fields are folded, so their character offsets do not map back onto the
 * pointed display text. Instead find the SHORTEST RUN OF WHOLE WORDS whose folded
 * form contains the query — word boundaries survive folding, individual character
 * offsets do not.
 *
 * Folding is per-character plus two-character sequences that never span a space, so
 * folding each word and rejoining is identical to folding the whole string.
 */
export function highlight(display: string, needle: string, mode: SearchMode): Highlight | null {
  if (!needle) return null
  const fold = mode === 'skeleton' ? skeleton : normalise
  const words = display.split(' ')
  const folded = words.map(fold)

  let bestFrom = -1
  let bestTo = -1
  for (let i = 0; i < words.length; i += 1) {
    for (let j = i; j < words.length; j += 1) {
      if (
        !folded
          .slice(i, j + 1)
          .join(' ')
          .includes(needle)
      )
        continue
      if (bestFrom === -1 || j - i < bestTo - bestFrom) {
        bestFrom = i
        bestTo = j
      }
      break // extending j only lengthens this window
    }
  }
  if (bestFrom === -1) return null

  return {
    before: words.slice(0, bestFrom).join(' ') + (bestFrom > 0 ? ' ' : ''),
    match: words.slice(bestFrom, bestTo + 1).join(' '),
    after: (bestTo < words.length - 1 ? ' ' : '') + words.slice(bestTo + 1).join(' '),
  }
}
